import React, {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from 'react';
import {assert} from 'assert-ts';
import type {DataValue, FieldError, PartCodeList} from 'schemaDefinition/types';
import type {CodeListId} from 'services/codelists/types';
import {useLocalization} from 'localization';
import {evaluateFieldRequired} from 'schemaDefinition/functions/evaluateFieldRequired';
import {useCodelist} from 'services/codelists';
import {
  CodeListField,
  Layout,
  RadioButtonsField,
  SortableCodeListField,
  Spacer,
  Text,
} from 'components';
import {useUniqueFieldId} from 'components/fields/hooks/useUniqueFieldId';
import type {BaseFieldProps} from './types';
import {useDataFormContext} from '../../contexts';
import {
  getErrorMessage,
  getSimpleCardinality,
  isMultiple,
} from '../../functions';
import {toFieldError, validateCodeList} from '../../functions/validators';

type Props = BaseFieldProps & {
  part: PartCodeList;
};

export const Select: React.FC<Props> = ({
  part,
  value,
  setFieldValue,
  valuePath,
  localScope,
  globalScope,
  relatedScope,
  label,
  placeholder,
  mode,
  showWhenReadonlyAndEmpty,
  layout,
  suggest,
}) => {
  const {codelistId, cardinality = 'single'} = part;
  const {tLoose} = useLocalization();
  const codelist = useCodelist(codelistId as CodeListId);
  const {codes: options} = codelist;
  const optionsWithCode = useMemo(
    () =>
      part.variant === 'buttons'
        ? options
        : options.map(opt => ({
            ...opt,
            label: `${opt.value} (${opt.code})`,
          })),
    [options, part.variant],
  );

  const handleSetFieldValue = useCallback(
    (value: DataValue) => setFieldValue(valuePath, value, part),
    [setFieldValue, valuePath, part],
  );

  const fieldId = useUniqueFieldId(valuePath);
  const [visited, setVisited] = useState(false);
  const onSetVisited = useEffectEvent(setVisited);
  const handleSetVisited = useCallback(() => {
    setVisited(true);
  }, []);
  const {showErrors} = useDataFormContext();

  const required = useMemo(() => {
    return evaluateFieldRequired(
      part.required,
      valuePath,
      localScope,
      globalScope,
      relatedScope,
      value,
    );
  }, [globalScope, part.required, relatedScope, localScope, value, valuePath]);

  // If the value is disabled, reevaluate the error.
  //  Triggered when e.g. work type is changed,
  //  which updates disabled on manifestation codelist items (e.g. PRODUCT_FORM).
  useEffect(() => {
    if (value && codelist.codes.find(c => c.code === value)?.disabled) {
      onSetVisited(true);
    }
  }, [codelist.codes, value]);

  const {error, errorMessage} = useMemo((): {
    error: FieldError;
    errorMessage?: string;
  } => {
    if (showErrors || visited) {
      const validation = validateCodeList(part, {
        value,
        valuePath,
        localScope,
        globalScope,
        relatedScope,
        codelistMap: {
          [codelist.id]: codelist,
        },
      });

      return {
        error: toFieldError(validation.valid),
        errorMessage: getErrorMessage(validation, tLoose),
      };
    }
    return {error: false};
  }, [
    codelist,
    globalScope,
    part,
    relatedScope,
    localScope,
    showErrors,
    tLoose,
    value,
    valuePath,
    visited,
  ]);

  const codeValue = (value ?? (isMultiple(cardinality) ? [] : null)) as
    | string
    | string[]
    | null;

  const suggestions = useMemo(() => {
    if (suggest) {
      return suggest(valuePath, part);
    }
  }, [part, suggest, valuePath]);

  if (part.variant === 'buttons' && cardinality === 'single') {
    assert(
      typeof codeValue === 'string' || codeValue === null,
      'Select: single value must be string or null',
      {value, part},
    );
    const singleCodeValue = codeValue as string | null;

    return (
      <RadioButtonsField
        id={fieldId}
        label={label}
        value={singleCodeValue}
        name={valuePath}
        options={optionsWithCode}
        showCode={part.showCode}
        required={required}
        readonly={mode === 'read-only'}
        error={error}
        onChange={handleSetFieldValue}
        onBlur={handleSetVisited}
        layout={layout}
        suggestions={suggestions}
      />
    );
  }

  return codelistId === 'COUNTRY' ? (
    isMultiple(cardinality) ? (
      <SortableCodeListField
        label={label}
        value={codeValue}
        cardinality={getSimpleCardinality(cardinality)}
        showCode={part.showCode}
        readonly={mode === 'read-only'}
        showWhenReadonlyAndEmpty={showWhenReadonlyAndEmpty}
        required={required}
        error={error}
        errorMessage={errorMessage}
        name={valuePath}
        id={fieldId}
        placeholder={placeholder}
        options={optionsWithCode}
        layout={layout}
        renderOptionContent={option => (
          <Layout horizontal>
            <img
              loading="lazy"
              width="20"
              src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
              srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
              alt=""
            />
            <Spacer width={1} />
            <Text variant="body2">{option.value}</Text>
          </Layout>
        )}
        onChange={handleSetFieldValue}
        onBlur={handleSetVisited}
        suggestions={suggestions}
      />
    ) : (
      <CodeListField
        label={label}
        value={codeValue}
        cardinality={getSimpleCardinality(cardinality)}
        showCode={part.showCode}
        readonly={mode === 'read-only'}
        showWhenReadonlyAndEmpty={showWhenReadonlyAndEmpty}
        required={required}
        error={error}
        errorMessage={errorMessage}
        name={valuePath}
        id={fieldId}
        placeholder={placeholder}
        options={optionsWithCode}
        layout={layout}
        renderOptionContent={option => (
          <Layout horizontal>
            <img
              loading="lazy"
              width="20"
              src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
              srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
              alt=""
            />
            <Spacer width={1} />
            <Text variant="body2" data-cy={`codelist-option-${option.code}`}>
              {option.value}
            </Text>
          </Layout>
        )}
        onChange={handleSetFieldValue}
        onBlur={handleSetVisited}
        suggestions={suggestions}
      />
    )
  ) : (
    <SortableCodeListField
      label={label}
      value={codeValue}
      cardinality={getSimpleCardinality(cardinality)}
      showCode={part.showCode}
      readonly={mode === 'read-only'}
      showWhenReadonlyAndEmpty={showWhenReadonlyAndEmpty}
      error={error}
      errorMessage={errorMessage}
      required={required}
      name={valuePath}
      id={fieldId}
      placeholder={placeholder}
      options={optionsWithCode}
      layout={layout}
      onChange={handleSetFieldValue}
      onBlur={handleSetVisited}
      suggestions={suggestions}
    />
  );
};
